import { useCallback, useEffect, useState } from "react";
import { UserPlus, Key, Prohibit, ArrowCounterClockwise, ShieldCheck, Clock, LinkSimple } from "@phosphor-icons/react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError } from "../lib/utils-extra";

const ROLES = [
  { value: "employee", label: "Employee" },
  { value: "manager", label: "Manager" },
  { value: "hr", label: "HR / Admin" },
  { value: "owner", label: "Owner" },
];

const fmtLogin = (iso) => {
  if (!iso) return "Never";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

/**
 * Phase 3 — User Access section inside the Employee Profile (owner/hr only).
 * Employee = person; User = login/access. Reuses existing /users lifecycle endpoints.
 */
export function EmployeeUserAccess({ employeeId, employeeName, employeeEmail }) {
  const [account, setAccount] = useState(undefined); // undefined=loading, null=none, obj=linked
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "employee" });
  const [busy, setBusy] = useState(false);
  const [tempInfo, setTempInfo] = useState(null); // {email, temp_password}
  const [confirm, setConfirm] = useState(null); // {type:'suspend'|'reactivate'|'reset', label}

  const load = useCallback(() => {
    api.get(`/employees/${employeeId}/user`)
      .then(({ data }) => setAccount(data.user || null))
      .catch(() => setAccount(null));
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ name: employeeName || "", email: employeeEmail || "", role: "employee" });
    setCreateOpen(true);
  };

  const createAccount = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/users", { ...form, employee_id: employeeId });
      setCreateOpen(false);
      if (data.temp_password) setTempInfo({ email: data.email, temp_password: data.temp_password });
      toast.success("Login account created");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const changeRole = async (role) => {
    try {
      await api.put(`/users/${account.id}`, { role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runConfirm = async () => {
    const { type } = confirm;
    setConfirm(null);
    try {
      if (type === "reset") {
        const { data } = await api.post(`/users/${account.id}/reset-password`, {});
        setTempInfo({ email: data.email, temp_password: data.temp_password });
        toast.success("Password reset");
      } else if (type === "suspend") {
        await api.post(`/users/${account.id}/suspend`, {});
        toast.success("Access suspended");
      } else if (type === "reactivate") {
        await api.post(`/users/${account.id}/reactivate`, {});
        toast.success("Access reactivated");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (account === undefined) {
    return <div className="py-8 text-center text-sm text-[#475569]">Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid="employee-user-access">
      <Card className="surface-card border-[#E2E8F0]">
        <CardContent className="p-5">
          <div className="font-display text-lg font-semibold flex items-center gap-2 mb-1">
            <ShieldCheck size={18} weight="duotone" color="#0EA5E9" /> User Access
          </div>
          <p className="text-xs text-[#475569] mb-4">Login &amp; security only. The employee record is managed separately.</p>

          {!account ? (
            <div className="text-center py-6" data-testid="no-account-state">
              <LinkSimple size={32} weight="duotone" color="#94A3B8" className="mx-auto mb-2" />
              <div className="font-medium">No login account linked</div>
              <p className="text-sm text-[#475569] mt-1">This employee cannot sign in yet.</p>
              <Button className="mt-4 bg-[#0EA5E9] hover:bg-[#0284C7]" onClick={openCreate} data-testid="create-login-button">
                <UserPlus size={16} className="mr-1" /> Create login account
              </Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="linked-account">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <div className="small-label">Login email</div>
                  <div className="text-sm font-medium mt-1" data-testid="account-email">{account.email}</div>
                  {employeeEmail && employeeEmail !== account.email && (
                    <div className="text-[11px] text-[#475569] mt-0.5">HR/contact email: {employeeEmail}</div>
                  )}
                </div>
                <div>
                  <div className="small-label">Account status</div>
                  <div className="mt-1">
                    <Badge className={account.status === "suspended" ? "bg-[#DC2626]/10 text-[#DC2626] border-0" : "bg-[#16A34A]/10 text-[#16A34A] border-0"} data-testid="account-status">
                      {account.status === "suspended" ? "Suspended" : "Active"}
                    </Badge>
                    {account.must_change_password && (
                      <span className="ml-2 text-[11px] text-[#D97706]">First login pending</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="small-label">Role</div>
                  <Select value={account.role} onValueChange={changeRole}>
                    <SelectTrigger className="mt-1.5 h-9" data-testid="account-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="small-label flex items-center gap-1"><Clock size={13} /> Last login</div>
                  <div className="text-sm font-medium mt-1" data-testid="account-last-login">{fmtLogin(account.last_login_at)}</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-2 border-t border-[#E2E8F0]">
                <Button variant="outline" size="sm" onClick={() => setConfirm({ type: "reset", label: account.email })} data-testid="reset-password-button">
                  <Key size={14} className="mr-1" /> Reset password
                </Button>
                {account.status === "suspended" ? (
                  <Button variant="outline" size="sm" onClick={() => setConfirm({ type: "reactivate", label: account.email })} data-testid="reactivate-button">
                    <ArrowCounterClockwise size={14} className="mr-1" /> Reactivate access
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" className="text-[#DC2626] hover:text-[#DC2626]" onClick={() => setConfirm({ type: "suspend", label: account.email })} data-testid="suspend-button">
                    <Prohibit size={14} className="mr-1" /> Suspend access
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create account dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-login-dialog">
          <DialogHeader><DialogTitle>Create login account</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Create a linked user account for this employee.</DialogDescription>
          <div className="space-y-4 py-1">
            <div>
              <Label className="small-label">Name</Label>
              <Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-login-name" />
            </div>
            <div>
              <Label className="small-label">Login email</Label>
              <Input type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="create-login-email" />
              <p className="text-[11px] text-[#475569] mt-1">Used to sign in. May differ from the employee&apos;s HR/contact email.</p>
            </div>
            <div>
              <Label className="small-label">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="create-login-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createAccount} disabled={busy} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="create-login-submit">
              {busy ? "Creating…" : "Create account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp password reveal */}
      <Dialog open={!!tempInfo} onOpenChange={(o) => !o && setTempInfo(null)}>
        <DialogContent className="max-w-md" data-testid="temp-password-dialog">
          <DialogHeader><DialogTitle>Temporary password</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-[#475569]">
            Share this with <span className="font-medium">{tempInfo?.email}</span>. They&apos;ll be required to change it on first login. This is shown only once.
          </DialogDescription>
          <div className="mt-2 p-3 rounded-xl bg-[#F1F5F9] font-mono-data text-center text-lg select-all" data-testid="temp-password-value">
            {tempInfo?.temp_password}
          </div>
          <DialogFooter>
            <Button onClick={() => setTempInfo(null)} className="bg-[#0EA5E9] hover:bg-[#0284C7]">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm suspend/reactivate/reset */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="account-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirm?.type === "reset" && "Reset password"}
              {confirm?.type === "suspend" && "Suspend access"}
              {confirm?.type === "reactivate" && "Reactivate access"}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-[#475569]">
            {confirm?.type === "reset" && <>Generate a new temporary password for <span className="font-medium">{confirm?.label}</span>? Their current password will stop working.</>}
            {confirm?.type === "suspend" && <>Suspend <span className="font-medium">{confirm?.label}</span>? They will be signed out and cannot log in until reactivated.</>}
            {confirm?.type === "reactivate" && <>Restore login access for <span className="font-medium">{confirm?.label}</span>?</>}
          </DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button onClick={runConfirm}
                    className={confirm?.type === "suspend" ? "bg-[#DC2626] hover:bg-[#B91C1C] text-white" : "bg-[#0EA5E9] hover:bg-[#0284C7]"}
                    data-testid="account-confirm-button">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EmployeeUserAccess;
